interface Task {
    task_id: string
    status: "completed" | "pending" | "failed"
    pasco_id: string
    error_message: string | null
    attempt_count: number
    created_at: string
    updated_at: string
}

interface RecentActivityProps {
    tasks: Task[]
    limit?: number
}

export function RecentActivity({ tasks, limit = 5 }: RecentActivityProps) {
    const recentTasks = tasks.slice(0, limit)

    const getStatusColor = (status: string) => {
        switch (status) {
            case "completed":
                return "text-success"
            case "pending":
                return "text-warning"
            case "failed":
                return "text-destructive"
            default:
                return "text-muted-foreground"
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case "completed":
                return (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                )
            case "pending":
                return (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                )
            case "failed":
                return (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                )
            default:
                return null
        }
    }

    return (
        <div className="rounded border border-border bg-card shadow-none hover:shadow-custom transition-shadow">
            <div className="border-b border-border p-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
                    <span className="text-sm text-muted-foreground">{recentTasks.length} recent tasks</span>
                </div>
            </div>
            <div className="divide-y divide-border">
                {recentTasks.length === 0 ? (
                    <div className="p-8 text-center">
                        <p className="text-sm text-muted-foreground">No recent activity</p>
                    </div>
                ) : (
                    recentTasks.map((task) => (
                        <div key={task.task_id} className="p-4 transition-colors hover:bg-muted/50">
                            <div className="flex items-start gap-4">
                                <div className={`mt-0.5 ${getStatusColor(task.status)}`}>{getStatusIcon(task.status)}</div>
                                <div className="flex-1 space-y-1">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-medium text-foreground">Task {task.task_id.slice(0, 8)}...</p>
                                        <span
                                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${task.status === "completed"
                                                ? "bg-success/10 text-success"
                                                : task.status === "pending"
                                                    ? "bg-warning/10 text-warning"
                                                    : "bg-destructive/10 text-destructive"
                                                }`}
                                        >
                                            {task.status}
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">PASCO: {task.pasco_id.slice(0, 8)}...</p>
                                    <p className="text-xs text-muted-foreground">Updated: {new Date(task.updated_at).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}
